import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Constants from "common/constants";
import { useTranslation } from "react-i18next";
import DataContainer from "../components/common/DataContainer/DataContainer";
import { useAuth } from "../hooks/useAuth";
import { useTableColumns } from "../hooks/useTableColumns";

import { fetchworkflowListRequest } from "store/slices/workflows/workflowListSlice";
import { startWorkflowProcessThunk, cloneWorkflowThunk, claimTaskThunk } from "store/thunks/workflowThunks";
import { Box, Typography, Divider } from "@mui/material";
import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import Loader from "../components/common/custom-component/Loader";
import { selectUserActions, selectUserWorkflowActions } from "store/slices/database/userAccessDataSlice";
import { logger } from "helpers/logger";
function WorkflowListPage() {
	// #region STATE
	const keycloakAuth = useAuth();
	const navigate = useNavigate();
	const [t] = useTranslation();
	const dispatch = useDispatch();

	const [isLoadingBackdrop, setIsLoadingBackdrop] = useState(false);
	const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState(false);

	const { data: WorkflowListData, loading, error } = useSelector((state) => ({
		data: state?.workflowList?.data?.data,
		loading: state?.workflowList?.loading,
		error: state?.workflowList?.error
	}), shallowEqual);
	const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData, shallowEqual);
	const userActions = useSelector(selectUserActions, shallowEqual);
	const userWorkflowActions = useSelector(selectUserWorkflowActions, shallowEqual);
	const userId = keycloakAuth.user.id;
	const hasClonePermission = userActions.includes("cloneWorkflow");
	// #endregion
	/* const renderCount = useRef(0);
	renderCount.current += 1;
	logger.info(`🔄 Component render #${renderCount.current}`); */
	// #region FETCH DATA

	useEffect(() => {
		dispatch(
			fetchworkflowListRequest({
				userId: userId,
				companyId: selectedCompany.companyId,
			})
		);
	}, [dispatch, userId]);

	useEffect(() => {
		logger.info('userWorkflowActions changed:', userWorkflowActions);
	}, [userWorkflowActions]);

	useEffect(() => {
		const hasExistingData = WorkflowListData?.workflowsResponseList?.length > 0;
		setIsBackgroundRefreshing(loading && hasExistingData);
	}, [loading, WorkflowListData?.workflowsResponseList?.length]);

	// #endregion

	// #region HANDLERS

	/* const parseIfJson = (data) => {
		try {
			const v = JSON.parse(data);
			return typeof v === "string" ? parseIfJson(v) : v;
		} catch {
			return data;
		}
	}; */

	// #endregion

	// #region Start Process
	const handleStartProcess = useCallback(async (processId) => {
		setIsLoadingBackdrop(true);
		const startProcessResponse = await dispatch(
			startWorkflowProcessThunk({
				definitionKey: processId,
				assignee:userId

			})
		);
		if (startWorkflowProcessThunk.fulfilled.match(startProcessResponse)) {
			let formData = startProcessResponse.payload.data.formData;
			const taskID = startProcessResponse.payload.data.getCurrentUserTaskResponse.getFormByTaskResponse.taskId;

			if (keycloakAuth.token) await dispatch(claimTaskThunk({ taskId: taskID, userId: userId }));

			//localStorage.setItem(Constants.CurrentWorkflowStep, JSON.stringify(formData));
			navigate("/form_view_renderer", { state: { data: formData } });
		} else {
			logger.error("Failed to start process");
			setIsLoadingBackdrop(false);
		}
	}, [dispatch, userId, keycloakAuth.token, navigate]);
	// #endregion

	// #region CLONE
	const handleCloneLastWorkflow = useCallback(async (applicationId) => {
		try {
			const result = await dispatch(cloneWorkflowThunk({ applicationId: applicationId, assignee: userId }));
			if (cloneWorkflowThunk.fulfilled.match(result) && result.payload?.data?.httpResponse.status == 1) {
				let formData = result.payload?.data?.startProcessResponse?.formData;
				localStorage.setItem(Constants.CurrentWorkflowStep, JSON.stringify(formData));
				navigate("/form_view_renderer", { state: { data: formData } });
			} else {
				logger.error("Failed to clone workflow");
				setIsLoadingBackdrop(false);
			}
		} catch (error) {
			logger.error(error);
			setIsLoadingBackdrop(false);
		}
	}, [dispatch, userId, navigate]);
	// #endregion

	// #region TABLE CONFIG

	const actions = useMemo(() => ({
		onStart: (row) => handleStartProcess(row.id),
		onClone: (row) => handleCloneLastWorkflow(row.key),
	}), [handleStartProcess, handleCloneLastWorkflow]);

	const context = useMemo(() => ({
		hasClonePermission,
		userWorkflowActions,
	}), [hasClonePermission, userWorkflowActions]);

	const { columns } = useTableColumns('WorkflowList', { actions, context });

	// #endregion

	// #region RENDER

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
				<Typography variant="h3" color="primary">
					{t("Workflow List")}
				</Typography>
				{isBackgroundRefreshing && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Loader variant="circular" message="Loading..." size="small" />
					</Box>
				)}
			</Box>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("View and manage available workflow processes.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{!WorkflowListData?.workflowsResponseList && loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
					<Loader variant="dots" message="Loading..." />
				</Box>
			) : !WorkflowListData?.workflowsResponseList || WorkflowListData?.workflowsResponseList?.length === 0 ? (
				<DataContainer data={[]} emptyStateIcon={CalendarIcon} emptyStateTitle="No Workflows Available" emptyStateSubtitle="" />
			) : (
				<Box sx={{ mt: 2 }}>
					<DataContainer data={WorkflowListData?.workflowsResponseList} columns={columns} showRowNumbers={true} maxHeight={600} stickyHeader={true} />
				</Box>
			)}
			<LoadingBackdrop open={isLoadingBackdrop} message="Loading data, please wait..." variant="bars" color="greenGradient" />
		</Box>
	);

	// #endregion
}

export default WorkflowListPage;
